import { useRef, useState } from "react";
import { UploadCloud, Lock } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { api, errorMessage } from "@/lib/api";
import { PropertySelect } from "./PropertySelect";

const MAX = 10 * 1024 * 1024;

export const UploadZone = ({ workspace, properties = [], onUploaded }) => {
  const input = useRef(null);
  const [pid, setPid] = useState(properties[0]?.id || null);
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");

  if (workspace.is_sample) return (
    <div data-testid="document-upload-disabled" className="flex items-start gap-3 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600">
      <Lock className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
      <p>Uploads are turned off in the sample workspace. Switch to your own property workspace to upload real documents.</p>
    </div>
  );

  const upload = async (file) => {
    setError("");
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "txt"].includes(ext)) return setError("Only PDF and TXT files can be uploaded right now.");
    if (file.size > MAX) return setError("This file is larger than 10 MB.");
    const body = new FormData();
    body.append("file", file);
    if (pid) body.append("property_id", pid);
    setProgress(0);
    try {
      await api.post(`/workspaces/${workspace.id}/documents`, body, { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)) });
      toast.success(`Saved "${file.name}"`);
      onUploaded();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setProgress(null);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div>
      {properties.length > 0 && (
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center"><span className="text-sm text-slate-600">Upload to property:</span>
          <div className="sm:w-72"><PropertySelect properties={properties} value={pid} onChange={setPid} testId="upload-property-select" className="h-10 bg-white" /></div></div>
      )}
      <button type="button" data-testid="document-upload-dropzone" onClick={() => input.current?.click()} disabled={progress !== null}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files[0]); }}
        className={`flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${drag ? "border-gold bg-gold-light/50" : "border-slate-300 bg-white hover:border-navy/40"}`}>
        <UploadCloud className="h-8 w-8 text-gold-dark" />
        <p className="mt-3 font-medium text-navy">{progress !== null ? "Uploading..." : "Drop a file here or click to choose"}</p>
        <p className="mt-1 text-xs text-slate-500">PDFs (including scans) or UTF-8 text files. Up to 10 MB and 30 pages. To turn photos of a form into a PDF, use "Photos to PDF".</p>
        {progress !== null && <Progress value={progress} className="mt-4 h-1.5 w-56" data-testid="document-upload-progress" />}
      </button>
      <input ref={input} type="file" accept=".pdf,.txt,application/pdf,text/plain" className="hidden" data-testid="document-upload-input" onChange={(e) => upload(e.target.files[0])} />
      {error && <p data-testid="document-upload-error" role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}
    </div>
  );
};
